"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card, CardContent } from "@/components/ui/card";
import Link from "next/link";
import {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Download, Monitor, Users, MapPin, FileText, ArrowRight,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Download, Monitor, Users, MapPin, FileText, ArrowRight,
  shield: Shield, zap: Zap, lock: Lock, "eye-off": EyeOff,
  globe: Globe, server: Server, wifi: Wifi, key: Key,
  download: Download, monitor: Monitor, users: Users,
  "map-pin": MapPin, "file-text": FileText, "arrow-right": ArrowRight,
};

interface RelatedPage {
  title: string;
  description?: string;
  href: string;
  icon?: string;
}

interface RelatedPagesProps {
  title?: string;
  pages: RelatedPage[];
}

const RelatedPages = ({ title, pages }: RelatedPagesProps) => {
  if (!pages || pages.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-muted/50">
      <div className="container max-w-5xl mx-auto">
        {title && (
          <div className="text-center mb-12">
            <BlurFade delay={0.1}>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                {title}
              </h2>
            </BlurFade>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pages.map((page, i) => {
            const Icon = page.icon ? (ICON_MAP[page.icon] || FileText) : FileText;
            return (
              <BlurFade key={i} delay={0.15 + i * 0.05}>
                <Link href={page.href} className="block h-full">
                  <Card className="border-foreground/5 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all h-full group">
                    <CardContent className="p-6 space-y-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                        <Icon size={20} className="text-foreground/70" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-bold text-foreground group-hover:text-foreground/80 transition-colors">
                        {page.title}
                      </h3>
                      {page.description && (
                        <p className="text-foreground/60 text-sm leading-relaxed">
                          {page.description}
                        </p>
                      )}
                      <div className="flex items-center gap-1 text-sm font-medium text-foreground/40 group-hover:text-foreground/60 transition-colors">
                        <span>Read more</span>
                        <ArrowRight size={14} />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedPages;
